import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Tooltip from '@mui/material/Tooltip'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import variables from './../theme/variables.scss'
import TablePagination from '@mui/material/TablePagination'
import Typography from '@mui/material/Typography'

import AppBar from '../components/app-bar'
import StepperTabs from '../components/portal-components/stepper-tabs/StepperTabs'
import ClinicalTrialTable from '../components/portal-components/ClinicalTrialTable'
// import { TablePaginationActions } from '../components/appointment-list/index'

import {
  clearTrial,
  getClinicListData_v1,
  getTrialListData_v1,
  getClinicPractitioners,
  clearClinicPractitioners,
} from '../redux/searchFlow/screenFlow/screenFlow.action'

const tableHeaderData = [
  { name: 'Study Title', align: null },
  { name: 'Sponsor', align: 'right' },
  { name: 'Eligibility Criteria', align: 'right' },
  { name: 'ARM', align: 'right' },
]

function SimpleDropdown({ dropDownList, onChangeHandler, labelValue, defaultValue }) {
  const [value, setValue] = useState(defaultValue ?? '')

  return (
    <>
      {labelValue && (
        <Typography className="fs-8" component={'span'}>
          {labelValue}
        </Typography>
      )}
      <Select
        value={value ?? defaultValue}
        onChange={(e) => {
          setValue(e.target.value)
          onChangeHandler(e.target.value)
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: variables.gray200,
              borderRadius: '8px',
              borderWidth: '2px !important',
            },
            '&:hover fieldset': {
              borderColor: variables.indigo300,
            },
            '&.Mui-focused fieldset': {
              borderColor: variables.indigo500,
            },
            '&.Mui-disabled fieldset': {
              borderColor: variables.gray200,
              backgroundColor: variables.whiteTransparent,
              pointerEvents: 'initial !important',
              cursor: 'not-allowed !important',
            },
          },
        }}
      >
        {dropDownList.map((dropDownItem) => (
          <MenuItem value={dropDownItem.value}>{dropDownItem.name}</MenuItem>
        ))}
      </Select>
    </>
  )
}

export const ScreenFlow = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const loginState = useSelector((state) => state.loginData.loginResponse)
  const token = useSelector((state) => state.loginData.loginResponse.idToken)
  const clinicList = useSelector((state) => state.screenFlowPortalClinicalTrial.clinicList)
  const clinicListLoading = useSelector((state) => state.screenFlowPortalClinicalTrial.clinicListLoading)
  const clinicPractitioners = useSelector((state) => state.screenFlowPortalClinicalTrial.clinicPractitioners)
  const clinicPractitionersLoading = useSelector(
    (state) => state.screenFlowPortalClinicalTrial.clinicPractitionersLoading
  )
  const clinicalTrialList = useSelector((state) => state.screenFlowPortalClinicalTrial.clinicalTrialList)
  const clinicalTrialListLoading = useSelector(
    (state) => state.screenFlowPortalClinicalTrial.clinicalTrialListLoading
  )

  const clinicListOptionArray = clinicList.map((clinic) => ({
    name: clinic.name,
    value: clinic.id,
  }))
  const clinicPractitionersOptionArray = clinicPractitioners?.map((practitioner) => ({
    name: practitioner.name,
    value: practitioner.id,
  }))

  const [clinicId, setClinicId] = useState('1') // 1 is default
  const [practitionerId, setPractitionerId] = useState('')
  const [togglePopUp, setTogglePopUp] = useState(false)
  const [stepValue, setStepValue] = useState('one')

  useEffect(() => {
    if (loginState.hasOwnProperty('accessToken') && loginState.accessToken === '') {
      navigate('/')
    }
  })

  useEffect(() => {
    const payloadData = {
      // page: 1,
      // pageLimit: 10,
      // searchKey: '',
      searchTerm: '',
    }
    dispatch(getClinicListData_v1(payloadData, token))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!clinicId) {
      return
    }

    const payloadData = {
      // page: 1,
      // pageLimit: 10,
      // searchKey: '',
      searchTerm: '',
      clinic_id: clinicId,
    }
    dispatch(getClinicPractitioners(payloadData, token))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId])

  useEffect(() => {
    if (!clinicPractitioners?.[0]?.id) {
      return
    }

    setPractitionerId(clinicPractitioners?.[0]?.id || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicPractitioners])

  useEffect(() => {
    if (!clinicId || !practitionerId) {
      return
    }

    const payloadData = {
      // page: 1,
      // pageLimit: 10,
      // searchKey: '',
      searchTerm: '',
      clinic_id: clinicId,
      practitioner_id: practitionerId,
    }
    dispatch(getTrialListData_v1(payloadData, token))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, practitionerId])

  const onClinicSelectHandler = (value) => {
    setClinicId(value)
    dispatch(clearClinicPractitioners())
    setPractitionerId('')
  }

  const onPractitionerSelectHandler = (value) => {
    setPractitionerId(value)
  }

  return (
    <>
      <div style={{ background: '#F3F4F6', minHeight: '100vH' }}>
        <AppBar searchPlaceholder={'Search Clinical Trials'} searchType={'screenflow'} hideSearch={false} />

        <Container maxWidth="lg">
          <Box className="my-1">
            <StepperTabs value={stepValue} setValue={setStepValue} />
          </Box>

          <Box className="my-1 d-flex align-items-center justify-content-start px-1 my-3">
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => {
                navigate('/portals')
                dispatch(clearTrial())
              }}
              aria-label="close"
            >
              <KeyboardBackspaceIcon sx={{ color: variables.purple }} />
            </IconButton>
            <Typography className="fs-5 fw-bold">Clinical Trial Screener</Typography>
          </Box>

          <Box className="pb-3 mt-1">
            <Box
              component={Paper}
              sx={{
                width: '100%',
                border: '1px solid',
                borderColor: variables.gray200,
                borderRadius: '8px',
                boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.04), 0px 1px 2px rgba(0, 0, 0, 0.06)',
                p: 2,
                display: 'flex',
                justifyContent: 'start',
                gap: 4,
              }}
            >
              <Box sx={{ width: '50%' }}>
                <Typography className="gray-600 fs-6 fw-normal pb-2">Clinic</Typography>
                <Tooltip
                  arrow
                  title="Filtering patient results according to the current logged in provider"
                  followCursor
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [0, 24],
                          },
                        },
                      ],
                    },
                  }}
                >
                  <SimpleDropdown
                    dropDownList={clinicListOptionArray}
                    onChangeHandler={onClinicSelectHandler}
                    // labelValue="Clinic"
                    defaultValue={'1'}
                  />
                </Tooltip>
              </Box>

              <Box sx={{ width: '50%' }}>
                <Typography className="gray-600 fs-6 fw-normal pb-2">Practitioner</Typography>
                <Tooltip
                  arrow
                  title="Select a practitioner"
                  followCursor
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [0, 24],
                          },
                        },
                      ],
                    },
                  }}
                >
                  <SimpleDropdown
                    dropDownList={clinicPractitionersOptionArray}
                    onChangeHandler={onPractitionerSelectHandler}
                    defaultValue={'1'}
                  />
                </Tooltip>
              </Box>
            </Box>
          </Box>

          <Box className="pb-4 mt-1">
            <ClinicalTrialTable
              tableHeaderData={tableHeaderData}
              loading={clinicListLoading || clinicPractitionersLoading || clinicalTrialListLoading}
              tableBodyData={clinicalTrialList}
              togglePopUp={togglePopUp}
              setTogglePopUp={setTogglePopUp}
            />
            <TablePagination
              labelDisplayedRows={(from = 1) =>
                `Displaying ${clinicalTrialList?.length ?? 0} out of ${
                  clinicalTrialList?.length ?? 0
                } results`
              }
              component="div"
              count={10}
              rowsPerPageOptions={[]}
              rowsPerPage={10}
              page={0}
              labelRowsPerPage="Displaying"
              SelectProps={{
                inputProps: {
                  'aria-label': 'Displaying',
                },
                native: true,
              }}
              onPageChange={(props) => {}}
              // ActionsComponent={TablePaginationActions}
            />
          </Box>
        </Container>
      </div>
    </>
  )
}

export default ScreenFlow
